import React, { useState } from "react";
import { Paper, Box, Typography, Grid, FormHelperText } from "@mui/material"
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import dayjs from "dayjs";
import CustomDatePicker from "../../../components/FormComponents/CustomDatePicker";
import RadioSelect from "../../../components/FormComponents/RadioSelect";
import CustomDropdown from "../../../components/FormComponents/CustomDropdown";
import { getCareerClusters } from "../../../remote/careerClusterRequests";
import { partnersRequest } from "../../../remote/partnersRequest";
import { degreesRequest } from "../../../remote/degreesRequest";
import { requestDeliveryFormats } from "../../../remote/deliveryFormatRequests";
import CustomTextField from "../../../components/FormComponents/CustomTextField";
import CustomSelect from "../../../components/FormComponents/SelectCourses";
import PurpleButton from "../../../components/LayoutComponents/PurpleButton";

export default function ProgramForm({ formData, setFormData, onSave, onDelete, session, loading, courses, selectedCourses, setSelectedCourses }) {    
    const [error, setError] = useState({});
    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };
    const handleSave = () => {
        let errors = {};
        if (!formData?.programName || formData?.programName === "") {
            errors.programName = "Program Name is required";
        }
        if (!formData?.programCode || formData?.programCode === "") {
            errors.programCode = "Program Code is required";
        }
        if (!formData?.effectiveStartDate && formData?.effectiveEndDate) {
            errors.effectiveStartDate = "Effective Start Date is required";
        }
        if (formData?.effectiveStartDate && !formData?.effectiveEndDate) {
            errors.effectiveEndDate = "Effective End Date is required";
        }
        if (formData?.effectiveStartDate > formData?.effectiveEndDate) {
            errors.effectiveEndDate = "Effective End Date must be after Effective Start Date";
        }
        if (formData?.effectiveStartDate <= Date.now()) {
            errors.effectiveEndDate = "Effective Start Date must be today or later.";
        }
        setError(errors);
        if (Object.keys(errors).length > 0){
            alert("Please fix the errors in the form before saving.");
            return;
        } 
        
        onSave();
    }

    return (
        <>
            <Paper sx={{ p: 3, display: "flex", flexDirection: "column", maxHeight: "60vh", overflowY: "auto" }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">
                        Program Details
                    </Typography>
                    {loading && <LoadingCircle size={20} />}
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField formData={formData} field="programName" handleChange={handleChange} multiline={false} rows={1} disabled={false} readOnly={false} label="Program Name" error={error.programName}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField formData={formData} field="programCode" handleChange={handleChange} multiline={false} rows={1} disabled={false} readOnly={false} label="Program Code" error={error.programCode} />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField formData={formData} field="programDescription" handleChange={handleChange} multiline={true} rows={3} disabled={false} readOnly={false} label="Program Description" />
                    </Grid>
                    <Grid item xs={12}>
                        <RadioSelect value={formData?.isActiveFlag} label={formData?.isActiveFlag ? "Active" : "Not Active"} onChange={(val) => handleChange("isActiveFlag", val)} />
                        <RadioSelect value={formData?.isFeatured} label={formData?.isFeatured ? "Featured" : "Not Featured"} onChange={(val) => handleChange("isFeatured", val)} />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ mt: 1}}>
                        <CustomSelect entities={courses.map(course => ({ id: course.courseId, name: course.courseName }))} selected={Array.isArray(selectedCourses)
                            ? selectedCourses.every(c => typeof c === "object" && c !== null && "courseId" in c)
                                ? selectedCourses.map(c => c.courseId)
                                : selectedCourses // If already array of IDs
                            : []} setSelected={setSelectedCourses} label="Select Courses" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField formData={formData} field="programContact" handleChange={handleChange} multiline={false} rows={1} disabled={false} readOnly={false} label="Program Contact" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField formData={formData} field="linkAdditionalInformation" handleChange={handleChange} multiline={false} rows={1} disabled={false} readOnly={false} label="Link Additional Information" />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField formData={formData} field="additionalInformation" handleChange={handleChange} multiline={true} rows={3} disabled={false} readOnly={false} label="Additional Information" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            label="Career Cluster"
                            entityIdField={"careerClusterID"}
                            entityName={"careerClusterName"}
                            value={formData?.careerClusterId}
                            setEntity={(val) => handleChange("careerClusterId", val)}
                            request={getCareerClusters}
                            error={!!error.careerClusterId}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            label="Education Facility"
                            entityIdField={"entityId"}
                            entityName={"entityName"}
                            value={formData?.entityId}
                            setEntity={(val) => handleChange("entityId", val)}
                            request={partnersRequest}
                            error={!!error.entityId}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            label="Degree Type"
                            entityIdField={"degreeTypeID"}
                            entityName={"degreeTypeName"}
                            value={formData?.degreeTypeId}
                            setEntity={(val) => handleChange("degreeTypeId", val)}
                            request={degreesRequest}
                            error={!!error.degreeTypeId}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDropdown
                            session={session}
                            label="Delivery Format"
                            entityIdField={"deliveryFormatID"}
                            entityName={"deliveryFormatName"}
                            value={formData?.deliveryFormatId}
                            setEntity={(val) => handleChange("deliveryFormatId", val)}
                            request={requestDeliveryFormats}
                            error={!!error.deliveryFormatId}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            selectedDate={dayjs(formData?.effectiveStartDate)}
                            setSelectedDate={(value) => handleChange("effectiveStartDate", value)}
                            label="Effective Start Date"
                            error={!!error.effectiveStartDate} />
                        <FormHelperText>{!!error.effectiveStartDate}</FormHelperText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomDatePicker
                            selectedDate={dayjs(formData?.effectiveEndDate)}
                            setSelectedDate={(value) => handleChange("effectiveEndDate", value)}
                            label="Effective End Date"
                            error={!!error.effectiveEndDate} />
                        <FormHelperText>{!!error.effectiveEndDate}</FormHelperText>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField formData={formData} field="creditHours" handleChange={handleChange} multiline={false} rows={1} disabled={false} readOnly={false} label="Credit Hours" type="number" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField formData={formData} field="programLengthMonths" handleChange={handleChange} multiline={false} rows={1} disabled={false} readOnly={false} label="Number of Months" type="number" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField formData={formData} field="programLengthYears" handleChange={handleChange} multiline={false} rows={1} disabled={false} readOnly={false} label="Number of Years" type="number" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <CustomTextField formData={formData} field="programLengthSemesters" handleChange={handleChange} multiline={false} rows={1} disabled={false} readOnly={false} label="Number of Semesters" type="number" />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField formData={formData} field="programPrerequisites" handleChange={handleChange} multiline={true} rows={3} disabled={false} readOnly={false} label="Pre Requirements" />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField formData={formData} field="programRequirements" handleChange={handleChange} multiline={true} rows={3} disabled={false} readOnly={false} label="Requirements" />
                    </Grid>
                </Grid>
            </Paper>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start', gap: 2 }}>
                <PurpleButton onClick={handleSave} label="Save" />
                <PurpleButton onClick={onDelete} label="Delete" />
            </Box>

        </>
    )
}